import type { ChecklistItem, StandardId } from '../types/standards-coverage.js'

/**
 * Spec-level review checklists derived from ISO 26262 / ISO/SAE 21434 / ISO 21448.
 *
 * Written in our own words: no standard text is quoted, and items are not mapped
 * to clause numbers. Each item states what a requirement specification should
 * contain and when the item applies. DRAFT — needs review by FuSa / CS experts.
 */
export const KNOWLEDGE_VERSION = '2026-10-10-draft-3'

type Item = Omit<ChecklistItem, 'standard'>

const ISO26262: Item[] = [
  {
    id: 'FS-01',
    topic: { ja: '安全関連機能の識別', en: 'Safety-related functions identified' },
    check: {
      ja: '安全に関わる機能（例：閉動作、挟み込み防止）が識別され、それ以外の機能と区別されている。',
      en: 'Safety-related functions (e.g. closing, anti-pinch) are identified and distinguished from other functions.',
    },
    applicability: { ja: '常に適用', en: 'Always applies' },
  },
  {
    id: 'FS-02',
    topic: { ja: '安全目標・ASILへの紐づけ', en: 'Link to safety goals / ASIL' },
    check: {
      ja: '安全関連要求が、上位の安全目標と ASIL（または QM）に紐づけられている。',
      en: 'Safety-related requirements are linked to a higher-level safety goal and an ASIL (or QM).',
    },
    applicability: { ja: '安全関連機能を含む場合', en: 'When the spec contains safety-related functions' },
  },
  {
    id: 'FS-03',
    topic: { ja: '安全状態の定義', en: 'Safe state defined' },
    check: {
      ja: '故障時に移行する安全状態（例：モーター停止、反転）が具体的に定義されている。',
      en: 'The safe state to enter on a fault (e.g. stop or reverse the motor) is concretely defined.',
    },
    applicability: { ja: '安全関連機能を含む場合', en: 'When the spec contains safety-related functions' },
  },
  {
    id: 'FS-04',
    topic: { ja: '故障検出', en: 'Fault detection' },
    check: {
      ja: '安全関連の故障（センサ、モーター、位置検出、電源など）の検出方法が定義されている。',
      en: 'Detection of safety-relevant faults (sensor, motor, position detection, power supply, etc.) is defined.',
    },
    applicability: { ja: '安全関連機能を含む場合', en: 'When the spec contains safety-related functions' },
  },
  {
    id: 'FS-05',
    topic: { ja: '故障反応時間', en: 'Fault reaction time' },
    check: {
      ja: '故障検出から安全状態到達までの時間が数値で定義されている。',
      en: 'The time from fault detection to reaching the safe state is specified numerically.',
    },
    applicability: { ja: '安全状態が必要な場合', en: 'When a safe state is required' },
  },
  {
    id: 'FS-06',
    topic: { ja: '縮退モード', en: 'Degraded mode' },
    check: {
      ja: '機能制限状態（例：自動閉の禁止、手動操作のみ）と、その移行条件・復帰条件が定義されている。',
      en: 'Degraded operation (e.g. auto-close disabled, manual only) and its entry and exit conditions are defined.',
    },
    applicability: { ja: '故障後も機能の一部を継続する場合', en: 'When part of the function continues after a fault' },
  },
  {
    id: 'FS-07',
    topic: { ja: '故障時の通知', en: 'Fault notification' },
    check: {
      ja: '故障・機能制限時に運転者や乗員へどのように通知するかが定義されている。',
      en: 'How the driver or occupants are informed of a fault or degraded mode is defined.',
    },
    applicability: { ja: '安全関連機能を含む場合', en: 'When the spec contains safety-related functions' },
  },
  {
    id: 'FS-08',
    topic: { ja: '安全関連入力の妥当性確認', en: 'Plausibility of safety-related inputs' },
    check: {
      ja: '安全関連入力（スイッチ、センサ、車速など）の範囲・妥当性の確認方法と、異常時の動作が定義されている。',
      en: 'Range / plausibility checks for safety-related inputs (switches, sensors, vehicle speed, etc.) and the behavior on invalid input are defined.',
    },
    applicability: { ja: '安全関連入力がある場合', en: 'When safety-related inputs exist' },
  },
  {
    id: 'FS-09',
    topic: { ja: '電源異常時の動作', en: 'Behavior on power anomalies' },
    check: {
      ja: '電圧低下・過電圧・電源断と復帰時の安全関連動作が定義されている。',
      en: 'Safety-related behavior on undervoltage, overvoltage, power loss and recovery is defined.',
    },
    applicability: { ja: '常に適用', en: 'Always applies' },
  },
  {
    id: 'FS-10',
    topic: { ja: '通信異常時の動作', en: 'Behavior on communication faults' },
    check: {
      ja: '安全関連信号の途絶・タイムアウト・異常値に対する動作が定義されている。',
      en: 'Behavior on loss, timeout or invalid values of safety-related signals is defined.',
    },
    applicability: { ja: '通信で安全関連信号を受け取る場合', en: 'When safety-related signals arrive over a network' },
  },
  {
    id: 'FS-11',
    topic: { ja: '意図しない作動の防止', en: 'Prevention of unintended actuation' },
    check: {
      ja: '要求がない状態での作動（例：意図しない閉動作）を防止する要求がある。',
      en: 'There is a requirement preventing actuation without a request (e.g. unintended closing).',
    },
    applicability: { ja: 'アクチュエータを駆動する場合', en: 'When an actuator is driven' },
  },
  {
    id: 'FS-12',
    topic: { ja: '単一故障への対策', en: 'Single-point fault measures' },
    check: {
      ja: '単一の故障で安全機能が失われないための診断・冗長・独立性のいずれかが記述されている。',
      en: 'Diagnostics, redundancy or independence that keep a single fault from defeating the safety function are described.',
    },
    applicability: {
      ja: 'ASIL（A〜D）が付与された要求がある場合。ASIL が記載されておらず適用可否を判断できない場合は判断不可とする',
      en: 'When requirements carry an ASIL (A–D). If no ASIL is stated, so applicability cannot be decided, the item is undeterminable',
    },
  },
  {
    id: 'FS-13',
    topic: { ja: '起動時・周期診断', en: 'Start-up and periodic diagnostics' },
    check: {
      ja: '起動時の自己診断や周期的な診断と、その異常時の動作が定義されている。',
      en: 'Start-up self-tests or periodic diagnostics and the reaction to their failure are defined.',
    },
    applicability: { ja: '安全関連機能を含む場合', en: 'When the spec contains safety-related functions' },
  },
  {
    id: 'FS-14',
    topic: { ja: '検証可能性', en: 'Verifiability' },
    check: {
      ja: '安全要求に、合否判定ができる数値・条件（検証基準）がある。',
      en: 'Safety requirements carry numeric values or conditions that allow a pass/fail verdict.',
    },
    applicability: { ja: '安全関連要求がある場合', en: 'When safety-related requirements exist' },
  },
  {
    id: 'FS-15',
    topic: { ja: '要求属性', en: 'Requirement attributes' },
    check: {
      ja: '安全要求に ID、ASIL、上位要求への参照、検証方法などの属性がある。',
      en: 'Safety requirements carry attributes such as ID, ASIL, parent reference and verification method.',
    },
    applicability: { ja: '安全関連要求がある場合', en: 'When safety-related requirements exist' },
  },
]

const ISO21434: Item[] = [
  {
    id: 'CS-01',
    topic: { ja: '保護対象資産の識別', en: 'Assets identified' },
    check: {
      ja: '保護すべき資産（制御コマンド、ソフトウェア、設定値、診断機能など）が識別されている。',
      en: 'Assets to protect (control commands, software, configuration, diagnostics, etc.) are identified.',
    },
    applicability: { ja: '常に適用', en: 'Always applies' },
  },
  {
    id: 'CS-02',
    topic: { ja: '外部インタフェースの識別', en: 'External interfaces identified' },
    check: {
      ja: '外部から到達できるインタフェース（CAN、車載 Ethernet、API、診断、OTA、無線など）が列挙されている。',
      en: 'Externally reachable interfaces (CAN, automotive Ethernet, API, diagnostics, OTA, wireless, etc.) are listed.',
    },
    applicability: { ja: '常に適用', en: 'Always applies' },
  },
  {
    id: 'CS-03',
    topic: { ja: '送信元の認証', en: 'Sender authentication' },
    check: {
      ja: '操作コマンド（特に遠隔・API からの開閉要求）の送信元を認証する要求がある。',
      en: 'There is a requirement to authenticate the sender of control commands (especially remote / API open-close requests).',
    },
    applicability: { ja: '外部から操作コマンドを受け付ける場合', en: 'When control commands are accepted from outside' },
  },
  {
    id: 'CS-04',
    topic: { ja: '認可・アクセス制御', en: 'Authorization and access control' },
    check: {
      ja: '誰が、どの操作を、どの車両状態で実行できるかが定義されている。',
      en: 'Who may perform which operation under which vehicle state is defined.',
    },
    applicability: { ja: '外部から操作コマンドを受け付ける場合', en: 'When control commands are accepted from outside' },
  },
  {
    id: 'CS-05',
    topic: { ja: '完全性とリプレイ対策', en: 'Integrity and replay protection' },
    check: {
      ja: 'メッセージの改ざん検知（MAC・署名など）と、リプレイ攻撃への対策（カウンタ、タイムスタンプなど）の両方が、攻撃者を想定したセキュリティ対策として定義されている。再送・重複の判別や通信異常対策など、機能・信頼性上の仕組みだけでは満たしたことにならない。',
      en: 'Both tamper detection (MAC, signature, etc.) and replay protection (counter, timestamp, etc.) are defined as security measures against an attacker. Functional or reliability mechanisms alone, such as telling retransmissions from new commands or handling communication faults, do not satisfy this.',
    },
    applicability: { ja: '通信でコマンドを受け取る場合', en: 'When commands arrive over a network' },
  },
  {
    id: 'CS-06',
    topic: { ja: '不正・異常コマンドの処理', en: 'Handling of invalid commands' },
    check: {
      ja: '形式不正・範囲外・未認証のコマンドを拒否することと、その際の動作が定義されている。故障状態や競合時にコマンドを無視する動作だけでは、不正コマンドの拒否とは見なさない。',
      en: 'Rejection of malformed, out-of-range or unauthenticated commands and the resulting behavior are defined. Ignoring commands during a fault or a conflict does not count as rejecting invalid commands.',
    },
    applicability: { ja: 'コマンドを受け付ける場合', en: 'When commands are accepted' },
  },
  {
    id: 'CS-07',
    topic: { ja: 'DoS・過負荷への耐性', en: 'DoS and overload resilience' },
    check: {
      ja: '大量の要求や通信負荷に対する挙動と、その間も安全機能を維持することが定義されている。',
      en: 'Behavior under request floods or bus overload, and that safety functions remain available meanwhile, is defined.',
    },
    applicability: { ja: '通信でコマンドを受け取る場合', en: 'When commands arrive over a network' },
  },
  {
    id: 'CS-08',
    topic: { ja: 'ソフトウェアの完全性', en: 'Software integrity' },
    check: {
      ja: 'セキュアブートや署名検証と、改ざんを検出した場合の動作が定義されている。',
      en: 'Secure boot or signature verification and the behavior when tampering is detected are defined.',
    },
    applicability: { ja: 'ソフトウェアを搭載する ECU の場合', en: 'When the ECU runs software' },
  },
  {
    id: 'CS-09',
    topic: { ja: 'ソフトウェア更新の保護', en: 'Protected software update' },
    check: {
      ja: '更新データの認証、ロールバック防止、更新失敗時の復旧が定義されている。',
      en: 'Authentication of update data, rollback prevention and recovery from failed updates are defined.',
    },
    applicability: { ja: 'ソフトウェア更新（OTA を含む）を行う場合', en: 'When software is updated (including OTA)' },
  },
  {
    id: 'CS-10',
    topic: { ja: '診断アクセスの保護', en: 'Protected diagnostic access' },
    check: {
      ja: '診断サービスやデバッグポートへのアクセス制御が定義されている。',
      en: 'Access control for diagnostic services and debug ports is defined.',
    },
    applicability: { ja: '診断機能を持つ場合', en: 'When diagnostic functions exist' },
  },
  {
    id: 'CS-11',
    topic: { ja: 'セキュリティイベントの記録', en: 'Security event logging' },
    check: {
      ja: '認証失敗などのセキュリティ異常を検知したときの記録・通知が定義されている。',
      en: 'Recording and reporting of detected security anomalies such as authentication failures are defined.',
    },
    applicability: { ja: '常に適用', en: 'Always applies' },
  },
  {
    id: 'CS-12',
    topic: { ja: '鍵・認証情報の管理', en: 'Key and credential management' },
    check: {
      ja: '鍵や認証情報の保管・更新・失効が定義されている。',
      en: 'Storage, renewal and revocation of keys and credentials are defined.',
    },
    applicability: { ja: '認証や暗号を用いる場合', en: 'When authentication or cryptography is used' },
  },
  {
    id: 'CS-13',
    topic: { ja: 'セキュリティと安全の整合', en: 'Security–safety consistency' },
    check: {
      ja: 'セキュリティ対策が働いたとき（認証失敗、コマンド拒否など）にも安全側の動作になることが定義されている。',
      en: 'It is defined that triggering a security measure (authentication failure, rejected command, etc.) still results in safe behavior.',
    },
    applicability: { ja: '安全関連機能を含む場合', en: 'When the spec contains safety-related functions' },
  },
  {
    id: 'CS-14',
    topic: { ja: '脆弱性への対応', en: 'Vulnerability handling' },
    check: {
      ja: '脆弱性の監視・対応と、その検証（侵入テストなど）に関する要求がある。',
      en: 'There are requirements for monitoring and handling vulnerabilities and for verifying them (e.g. penetration testing).',
    },
    applicability: { ja: '常に適用', en: 'Always applies' },
  },
]

const ISO21448: Item[] = [
  {
    id: 'SO-01',
    topic: { ja: '意図機能と前提条件', en: 'Intended function and preconditions' },
    check: {
      ja: '意図した機能と、前提とする使用条件（車両状態、乗員、環境）が定義されている。',
      en: 'The intended function and its assumed operating conditions (vehicle state, occupants, environment) are defined.',
    },
    applicability: { ja: '常に適用', en: 'Always applies' },
  },
  {
    id: 'SO-02',
    topic: { ja: '使用シナリオ', en: 'Usage scenarios' },
    check: {
      ja: '通常の使用シナリオ（例：ワンタッチ開閉、遠隔閉、施錠連動閉）が列挙されている。',
      en: 'Normal usage scenarios (e.g. one-touch open/close, remote close, close on locking) are listed.',
    },
    applicability: { ja: '常に適用', en: 'Always applies' },
  },
  {
    id: 'SO-03',
    topic: { ja: '予見可能な誤使用', en: 'Foreseeable misuse' },
    check: {
      ja: '予見可能な誤使用（子どもの操作、窓から身を乗り出す、物を挟むなど）が考慮されている。',
      en: 'Foreseeable misuse (operation by children, leaning out of the window, objects in the opening, etc.) is considered.',
    },
    applicability: { ja: '常に適用', en: 'Always applies' },
  },
  {
    id: 'SO-04',
    topic: { ja: '検知性能', en: 'Detection performance' },
    check: {
      ja: '検知機能（例：挟み込み検知）の検知対象、しきい値、検知時間が数値で定義されている。',
      en: 'Detection targets, thresholds and detection time of sensing functions (e.g. pinch detection) are specified numerically.',
    },
    applicability: { ja: '検知・認識機能がある場合', en: 'When sensing / recognition functions exist' },
  },
  {
    id: 'SO-05',
    topic: { ja: '誤検知', en: 'False positives' },
    check: {
      ja: '本来は検知すべきでない状況（凍結、摺動抵抗の増加、悪路での振動など）で誤検知した場合の扱いが定義されている。',
      en: 'Handling of false detection in situations that should not trigger (icing, increased friction, rough-road vibration, etc.) is defined.',
    },
    applicability: { ja: '検知・認識機能がある場合', en: 'When sensing / recognition functions exist' },
  },
  {
    id: 'SO-06',
    topic: { ja: '見逃し', en: 'Missed detection' },
    check: {
      ja: '検知しにくい対象（細い・柔らかい物体、指、傘、子どもの腕など）の扱いが定義されている。',
      en: 'Handling of hard-to-detect objects (thin or soft objects, fingers, umbrellas, a child’s arm, etc.) is defined.',
    },
    applicability: { ja: '検知・認識機能がある場合', en: 'When sensing / recognition functions exist' },
  },
  {
    id: 'SO-07',
    topic: { ja: '環境条件の境界', en: 'Environmental boundaries' },
    check: {
      ja: '性能が変化しうる環境条件（温度、凍結、水、汚れ、経年劣化）とその範囲が定義されている。',
      en: 'Environmental conditions that can change performance (temperature, icing, water, dirt, ageing) and their ranges are defined.',
    },
    applicability: { ja: '常に適用', en: 'Always applies' },
  },
  {
    id: 'SO-08',
    topic: { ja: '電圧・負荷条件の境界', en: 'Voltage and load boundaries' },
    check: {
      ja: '電圧変動や負荷変動のもとでの検知性能が定義されている。',
      en: 'Detection performance under voltage and load variation is defined.',
    },
    applicability: { ja: '検知・認識機能がある場合', en: 'When sensing / recognition functions exist' },
  },
  {
    id: 'SO-09',
    topic: { ja: '境界・遷移状態', en: 'Boundary and transition states' },
    check: {
      ja: '全閉付近、反転直後、連続操作、複数スイッチの同時操作などの境界状況での動作が定義されている。',
      en: 'Behavior in boundary situations such as near full close, right after reversal, repeated operation, or simultaneous switches is defined.',
    },
    applicability: { ja: '常に適用', en: 'Always applies' },
  },
  {
    id: 'SO-10',
    topic: { ja: '性能限界時の動作', en: 'Behavior at performance limits' },
    check: {
      ja: '性能限界を超えた、または判定できないときの動作（例：自動閉の禁止、低速化）が定義されている。',
      en: 'Behavior when performance limits are exceeded or a decision is not possible (e.g. disable auto-close, slow down) is defined.',
    },
    applicability: { ja: '検知・認識機能がある場合', en: 'When sensing / recognition functions exist' },
  },
  {
    id: 'SO-11',
    topic: { ja: '未知の危険シナリオへの対応', en: 'Handling unknown hazardous scenarios' },
    check: {
      ja: '市場データなどから未知の危険シナリオを見つけ、仕様に反映する仕組みが記述されている。',
      en: 'A mechanism to find unknown hazardous scenarios (e.g. from field data) and feed them back into the spec is described.',
    },
    applicability: {
      ja: '量産後の運用や OTA による変更を想定する場合',
      en: 'When post-launch operation or OTA changes are expected',
    },
  },
  {
    id: 'SO-12',
    topic: { ja: '性能の検証条件', en: 'Performance verification conditions' },
    check: {
      ja: '性能要求について、試験条件（試験物体、環境、合否基準）が定義されている。',
      en: 'Test conditions (test objects, environment, pass criteria) are defined for performance requirements.',
    },
    applicability: { ja: '性能要求がある場合', en: 'When performance requirements exist' },
  },
]

const withStandard = (standard: StandardId, items: Item[]): ChecklistItem[] =>
  items.map(item => ({ ...item, standard }))

export const STANDARD_CHECKLISTS: Record<StandardId, ChecklistItem[]> = {
  iso26262: withStandard('iso26262', ISO26262),
  iso21434: withStandard('iso21434', ISO21434),
  iso21448: withStandard('iso21448', ISO21448),
}
